import { useEffect, useState } from "react"
import ButtonWrap from "../../components/template/ButtonWrap"
import MainTitle from "../../components/template/MainTitle"
import Spinner from "../../components/template/Spinner"
import NewInfoFormat from "../../components/template/NewInfoFormat"
import NewInfoSelector from "../../components/template/NewInfoSelector"
import NewInfoDescription from "../../components/template/NewInfoDescription"
import api from "../../components/utils/api"
import { TRAINING_MODES } from "./trainingOptions"

// One submission trains exactly one model (architecture x sample rate);
// submit again to train more models, each cancellable on its own.
const TrainingTriggerModal = ({ experiment, models, closeModal, onTriggered }) => {
    const [queueStatus, setQueueStatus] = useState(null)
    const [isQueueStatusFailed, setIsQueueStatusFailed] = useState(false)
    const [architectureOptions, setArchitectureOptions] = useState([])
    const [sampleRateOptions, setSampleRateOptions] = useState([])
    const [mode, setMode] = useState("single")
    const [architecture, setArchitecture] = useState(null)
    const [sampleRate, setSampleRate] = useState(null)
    const [teachers, setTeachers] = useState([])
    const [errorMessage, setErrorMessage] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        const fetchQueueStatus = async () => {
            try {
                const res = await api.get('/api/gpu-queue/status')
                setQueueStatus(res.data)
            } catch (error) {
                console.error(error)
                setIsQueueStatusFailed(true)
            }
        }
        // the supported lists come from the backend validation source of
        // truth, so the dropdowns cannot offer a value the API would reject
        const fetchTrainingOptions = async () => {
            try {
                const res = await api.get('/api/experiments/training-options')
                setArchitectureOptions(res.data.architectures.map(name => ({ label: name, value: name })))
                setSampleRateOptions(res.data.sample_rates.map(rate => ({ label: rate, value: rate })))
            } catch (error) {
                console.error(error)
                setErrorMessage("Failed to load training options. Close the dialog and try again.")
            }
        }
        fetchQueueStatus()
        fetchTrainingOptions()
    }, [])

    const teacherOptions = models.map(m => ({ label: m.model_name, value: m.model_name }))
    // manual trainings are rejected upstream with 409 while a QA process runs
    const isQaBlocked = queueStatus?.qa_in_progress === true
    // resolve the queue state first so the QA warning is seen before the form
    // and the banner doesn't push the form down after the fact
    const isQueueStatusLoading = !queueStatus && !isQueueStatusFailed

    const queueBanner = () => {
        if (isQueueStatusFailed) return (
            <div className="mb-4 bg-gray-50 border border-gray-200 text-gray-500 text-sm rounded px-4 py-2 text-left">
                GPU queue status unavailable. You can still submit the training.
            </div>
        )
        if (!queueStatus) return null
        if (isQaBlocked) return (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded px-4 py-2 text-left">
                A QA process is running or queued on the GPU server. Manual training is blocked until it finishes.
            </div>
        )
        const runningCount = (queueStatus.running || []).length
        const pendingCount = (queueStatus.pending_manual || []).length + (queueStatus.pending_qa || []).length
        return (
            <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 text-sm rounded px-4 py-2 text-left">
                {!queueStatus.workers_reachable && <div className="text-yellow-700">GPU workers are unreachable right now; the queue state may be incomplete.</div>}
                {runningCount + pendingCount === 0
                    ? "The GPU server is idle — the training will start right away."
                    : `${runningCount} job(s) running, ${pendingCount} waiting on the GPU server. The training will queue behind them.`}
            </div>
        )
    }

    const onSubmit = async () => {
        setErrorMessage("")
        // validate up front so the user gets immediate feedback instead of a bare 400
        if (!architecture || !sampleRate) {
            setErrorMessage("Select an architecture and a sample rate.")
            return
        }
        if (mode === "distill" && teachers.length === 0) {
            setErrorMessage("Select at least one teacher model, or switch to Single.")
            return
        }
        const body = {
            mode,
            architecture: architecture.value,
            sample_rate: sampleRate.value,
            ...(mode === "distill" && { teachers: teachers.map(t => t.value) }),
        }
        setIsSubmitting(true)
        try {
            const res = await api.post(`/api/experiments/${experiment.id}/trainings`, body)
            onTriggered(res.data)
            closeModal()
        } catch (error) {
            console.error(error)
            setErrorMessage(error.response?.data?.message || "Failed to trigger the training. Please try again.")
        } finally {
            setIsSubmitting(false)
        }
    }

    if (isQueueStatusLoading) return (
        <div className='h-full'>
            <MainTitle title="New Training" />
            <div className="my-6"><Spinner /></div>
            <div className="text-sm text-gray-500">Checking the GPU queue status...</div>
        </div>
    )

    return (
        <div className='h-full'>
            <MainTitle title="New Training" />
            {queueBanner()}
            <div>
                <NewInfoFormat
                    label="Training Mode"
                    description={<NewInfoDescription contents={[
                        TRAINING_MODES.single.description,
                        TRAINING_MODES.distill.description,
                    ]} />}
                    contents={
                        <div className="text-left flex gap-6">
                            {Object.entries(TRAINING_MODES).map(([value, { label }]) => (
                                <label key={value} className="text-sm flex items-center gap-1">
                                    <input type="radio" name="trainingMode" checked={mode === value} onChange={() => setMode(value)} />
                                    {label}
                                </label>
                            ))}
                        </div>
                    }
                />
                <div className='md:grid md:grid-cols-2 md:gap-3'>
                    <NewInfoFormat
                        label="Architecture"
                        contents={<NewInfoSelector
                            value={architecture}
                            setValue={setArchitecture}
                            options={architectureOptions}
                            category=""
                            noOptionsMessage="Loading..."
                        />}
                        description={<NewInfoDescription contents={["Model architecture to train."]} />}
                    />
                    <NewInfoFormat
                        label="Sample Rate"
                        contents={<NewInfoSelector
                            value={sampleRate}
                            setValue={setSampleRate}
                            options={sampleRateOptions}
                            category=""
                            noOptionsMessage="Loading..."
                        />}
                        description={<NewInfoDescription contents={["Audio sample rate the model is trained at."]} />}
                    />
                </div>
                {mode === "distill" && <NewInfoFormat
                    label="Teacher Models"
                    contents={<NewInfoSelector
                        value={teachers}
                        setValue={setTeachers}
                        options={teacherOptions}
                        category=""
                        isMulti={true}
                        noOptionsMessage="No trained models for this experiment yet — train a teacher first"
                    />}
                    description={<NewInfoDescription contents={[
                        "Already-trained models of this experiment to distill from. Train a teacher with Single mode first if none exist.",
                    ]} />}
                />}
            </div>
            {errorMessage && <div className='mt-4 text-sm text-red-500 text-right'>{errorMessage}</div>}
            <div className='my-4 text-right'>
                <ButtonWrap contents="Run" onClick={onSubmit} isDisabled={isQaBlocked || isSubmitting} />
            </div>
        </div>
    )
}

export default TrainingTriggerModal
